import { forwardRef, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  label?: string
  error?: string
  hint?: string
  wrapperClassName?: string
  className?: string
  /** Valor numérico (em reais, não centavos). null = vazio. */
  value: number | null
  /** Callback recebe number puro (em reais). null se vazio. */
  onChange: (next: number | null) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  name?: string
  id?: string
  clearable?: boolean
  /** Símbolo do prefixo (default 'R$ '). */
  prefix?: string
}

/**
 * Input para valores monetários BR.
 *
 *  - Exibição com máscara `1.234,56` (separador de milhar + decimal vírgula)
 *  - Prefixo "R$ " visual à esquerda
 *  - Algoritmo: trata internamente como centavos (último dígito = unidade
 *    do centavo). A cada tecla, recalcula a string formatada.
 *  - onChange entrega `number` em reais (ex.: 1234.56), nunca string
 *  - Botão X para limpar
 */
export const CurrencyInput = forwardRef<HTMLInputElement, Props>(function CurrencyInput({
  label, error, hint, wrapperClassName, className,
  value, onChange, onBlur,
  placeholder = '0,00', disabled, required, name, id,
  clearable = true, prefix = 'R$ ',
}, ref) {
  const hasError = Boolean(error)

  const format = (num: number | null): string => {
    if (num === null || num === undefined || Number.isNaN(num)) return ''
    const cents = Math.round(num * 100)
    return formatCentsBR(cents)
  }
  const [text, setText] = useState<string>(() => format(value))
  const editingRef = useRef(false)

  useEffect(() => {
    if (editingRef.current) return
    setText(format(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    editingRef.current = true
    const onlyDigits = e.target.value.replace(/\D+/g, '')
    if (onlyDigits === '') {
      setText('')
      onChange(null)
      return
    }
    // Remove zeros à esquerda para evitar `00000123,45`
    const trimmed = onlyDigits.replace(/^0+(?=\d)/, '')
    const cents = parseInt(trimmed, 10)
    const formatted = formatCentsBR(cents)
    setText(formatted)
    onChange(cents / 100)
  }

  function handleBlur() {
    editingRef.current = false
    setText(format(value))
    onBlur?.()
  }

  function clear() {
    onChange(null)
    setText('')
  }

  const showClear = clearable && text !== '' && !disabled

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1" htmlFor={id}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}
      <div className={cn(
        'relative flex items-center bg-white rounded-md ring-1 transition',
        hasError ? 'ring-rose-300 focus-within:ring-rose-500' : 'ring-gray-200 focus-within:ring-brand-500 focus-within:ring-2',
        disabled && 'opacity-50 bg-gray-50',
      )}>
        <span className="absolute left-3 text-xs text-gray-500 select-none pointer-events-none">{prefix}</span>
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          className={cn(
            'w-full bg-transparent rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none',
            'pl-10 pr-9 py-2 text-right',
            className,
          )}
        />
        {showClear && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Limpar"
            onClick={clear}
            className="absolute right-2 cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {(error || hint) && (
        <span className={cn('text-[11px] mt-1', hasError ? 'text-rose-600' : 'text-gray-500')}>
          {error || hint}
        </span>
      )}
    </div>
  )
})

function formatCentsBR(cents: number): string {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents).toString().padStart(3, '0')
  const integer = abs.slice(0, -2)
  const decimal = abs.slice(-2)
  const withDots = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `${sign}${withDots},${decimal}`
}
